import { inject, Injectable } from '@angular/core';

import { CanvasElement } from '@blaze-runtime/engine-domain';
import { ColumnInfo } from '@blaze-shared/api-contracts-domain';
import { getActiveBinding } from '@blaze-shared/elements-domain';
import { SchemaService } from '@blaze-shared/schema-data-access';
import {
  BlazeSchema,
  convertBlazeSchemaToElementSchema,
  type ElementSchema,
  type RuntimeEventType,
} from '@blaze-shared/schema-domain';

import { RuntimeFacade } from '../../../+state/runtime/runtime.facade';
import { RuntimeService } from '../../../services/runtime.service';
import { findParentFormGroupLikeOfForm } from '../../../utils/layout/relationship';
import { hasDataProps } from '../../element-model/element-props-guards';
import { isFormList } from '../../element-model/element-type-guards';

const DELETED_COLUMN = {
  name: '_isDeleted',
  columnType: 'Boolean',
  dataType: 'Boolean',
} as unknown as ColumnInfo;

@Injectable({ providedIn: 'root' })
export class ElementOutputSchemaService {
  private readonly schemaService = inject(SchemaService);
  private readonly runtimeService = inject(RuntimeService);
  private readonly runtimeFacade = inject(RuntimeFacade);

  getElementOutputSchema(
    element: CanvasElement,
    eventType: RuntimeEventType,
    defaultSchema?: ElementSchema | null,
  ): ElementSchema | undefined {
    const schema = this.resolveRawSchema(element, defaultSchema);
    const schemaWithDeletedFlag = isFormList(element) ? this.appendDeletedColumn(schema) : schema;

    return schemaWithDeletedFlag
      ? convertBlazeSchemaToElementSchema(schemaWithDeletedFlag, element.id, eventType)
      : undefined;
  }

  private resolveRawSchema(
    element: CanvasElement,
    defaultSchema?: ElementSchema | null,
  ): BlazeSchema | null | undefined {
    let valueData = hasDataProps(element)
      ? getActiveBinding(element.props.data, 'value')
      : undefined;

    if (valueData?.dataSource?.sourceType === 'formGroup') {
      const formGroup = findParentFormGroupLikeOfForm(this.runtimeFacade.elementsValue, element.id);
      if (!formGroup) {
        throw new Error('form group like parent not found');
      }
      valueData = hasDataProps(formGroup)
        ? getActiveBinding(formGroup.props.data, 'value')
        : undefined;
    }

    const { dataSource, bindings } = valueData ?? {};

    if (dataSource?.sourceType === 'dataset' && dataSource.id) {
      return this.resolveDatasetSchema(dataSource.id, bindings?.['value']);
    }

    if (dataSource?.sourceType === 'context' && dataSource.id) {
      return this.runtimeService.getSchemaForRuntimeElement(
        { layerType: 'Workflow', id: dataSource.id },
        undefined,
      );
    }

    if (!dataSource?.sourceType) {
      return defaultSchema;
    }

    return null;
  }

  private resolveDatasetSchema(
    datasetId: string,
    columnKey: string | undefined,
  ): BlazeSchema | null {
    const column = this.schemaService.getColumnFromDataset(datasetId, columnKey ?? '');
    if (!column) {
      return null;
    }

    return {
      columns: [column],
      displayColumn: '',
      id: '',
      name: '',
      tableId: '',
      tableIds: [],
      tableName: '',
      workflowId: '',
    };
  }

  private appendDeletedColumn(
    schema: BlazeSchema | null | undefined,
  ): BlazeSchema | null | undefined {
    if (!schema) {
      return schema;
    }
    return { ...schema, columns: [...schema.columns, DELETED_COLUMN] };
  }
}
